import React, { useCallback, useEffect, useRef, useState } from 'react';

interface PromptOptions {
  title: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** true → Bestätigen ohne Text liefert '' statt null (Eingabe optional;
   *  null bleibt eindeutig „abgebrochen"). */
  allowEmpty?: boolean;
}

interface PromptState extends PromptOptions {
  open: boolean;
  value: string;
}

const TEAL = '#3fced1';

/**
 * Promise-basierte Text-Eingabe über ein zentriertes Modal — Ersatz für
 * window.prompt. Gibt den getrimmten Text zurück oder null bei Abbruch.
 *
 *   const { prompt, promptDialog } = usePrompt();
 *   const value = await prompt({ title: 'Verband hinzufügen', label: 'Verband' });
 *   if (value) { ... }
 *   return (<>{promptDialog}{restlicheUI}</>);
 */
export function usePrompt() {
  const [state, setState] = useState<PromptState>({ open: false, title: '', value: '' });
  const resolver = useRef<((value: string | null) => void) | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    setState({ ...options, open: true, value: options.initialValue ?? '' });
    return new Promise<string | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: string | null) => {
    setState((prev) => ({ ...prev, open: false }));
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  useEffect(() => {
    if (state.open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [state.open]);

  const submit = () => {
    const trimmed = state.value.trim();
    if (trimmed.length === 0) {
      settle(state.allowEmpty ? '' : null);
      return;
    }
    settle(trimmed);
  };

  const promptDialog = state.open ? (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={state.title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) settle(null);
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-5 text-white shadow-2xl">
        <h3 className="text-base font-bold">{state.title}</h3>
        {state.label && <label className="mt-3 block text-sm text-gray-400">{state.label}</label>}
        <input
          ref={inputRef}
          type="text"
          value={state.value}
          placeholder={state.placeholder}
          onChange={(e) => setState((prev) => ({ ...prev, value: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') settle(null);
          }}
          className="mt-1.5 w-full rounded-xl border border-zinc-700 bg-zinc-950 px-3.5 py-2.5 text-sm text-white placeholder:text-gray-500 focus:border-[#3fced1] focus:outline-none"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => settle(null)}
            className="rounded-xl border border-zinc-700 px-4 py-2 text-sm font-semibold text-gray-300 transition hover:border-[#3fced1]/60"
          >
            {state.cancelLabel ?? 'Abbrechen'}
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-black transition hover:opacity-90"
            style={{ backgroundColor: TEAL }}
          >
            {state.confirmLabel ?? 'Hinzufügen'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { prompt, promptDialog };
}

export default usePrompt;
